import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';
import { listPortalNotifications, markPortalNotificationsRead } from '../../api/portal';

/** The student's unread notifications (e.g. a rejected document and why),
 * shown at the top of the portal until dismissed. */
export const PortalNotifications = ({ onOpenDocuments }: { onOpenDocuments: () => void }) => {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['portal', 'notifications'], queryFn: listPortalNotifications });
  const markRead = useMutation({
    mutationFn: markPortalNotificationsRead,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['portal', 'notifications'] }),
  });

  const unread = (query.data ?? []).filter((n) => !n.readAt);
  if (unread.length === 0) return null;

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      action={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {unread.some((n) => n.type === 'DOCUMENT_REJECTED') && (
            <Button color="inherit" size="small" onClick={onOpenDocuments}>
              Open documents
            </Button>
          )}
          <Button color="inherit" size="small" disabled={markRead.isPending} onClick={() => markRead.mutate()}>
            Dismiss
          </Button>
        </Stack>
      }
    >
      <AlertTitle>
        {unread.length === 1 ? unread[0].title : `You have ${unread.length} new notifications`}
      </AlertTitle>
      <Stack spacing={0.5}>
        {unread.map((n) => (
          <Typography key={n.id} variant="body2">
            {unread.length > 1 && <strong>{n.title}: </strong>}
            {n.message}
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              · {new Date(n.createdAt).toLocaleString()}
            </Typography>
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
};
